import { useCallback } from 'react';

const SESSION_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Returns true if the current page load is a browser reload (F5 / Cmd+R).
 * Uses the Navigation Timing API which reliably distinguishes reload from fresh
 * navigation even when browsers preserve window.history.state across refreshes.
 */
export const isPageReload = () => {
  try {
    return performance.getEntriesByType('navigation')[0]?.type === 'reload';
  } catch (_) {
    return false;
  }
};

/**
 * Hook for persisting practice session (sessionData + questions) across page refreshes.
 * Used at the PAGE level. Components separately persist answers+index by session-id.
 *
 * Strategy:
 *   - Page reload OR no API params available: restore from localStorage.
 *   - Fresh navigation with params: fetch from API, overwrite localStorage.
 */
export const usePracticeSession = (practiceType) => {
  const STORAGE_KEY = `practice-session:${practiceType}`;

  const saveSession = useCallback((sessionData, questions, locationState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        sessionData,
        questions,
        locationState: locationState || null,
        savedAt: Date.now(),
      }));
    } catch (_) {}
  }, [STORAGE_KEY]);

  const loadSession = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.savedAt || Date.now() - parsed.savedAt > SESSION_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed; // { sessionData, questions, locationState, savedAt }
    } catch (_) {
      return null;
    }
  }, [STORAGE_KEY]);

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }, [STORAGE_KEY]);

  return { saveSession, loadSession, clearSession };
};

export default usePracticeSession;
